import React, { useState } from 'react';
import { Mail, MessageCircle, Send, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { contactInfo } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (event) => {
    setFormData((current) => ({
      ...current,
      [event.target.name]: event.target.value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const message = [
      'Hello EFAM, I would like help choosing a system.',
      '',
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone}`,
      formData.message ? `Message: ${formData.message}` : ''
    ].filter(Boolean).join('\n');

    const whatsappUrl = `${contactInfo.whatsappLink}?text=${encodeURIComponent(message)}`;
    const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    if (!newWindow) {
      window.location.href = whatsappUrl;
    }

    toast.success('Your enquiry is ready in WhatsApp. Press send there to deliver it to EFAM.');
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-slate-950 to-black relative overflow-hidden">
      <div className="absolute inset-0 grid-background opacity-30"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="contact-title">
              Choose Your <span className="text-blue-400 neon-text">System</span>
            </h2>
            <p className="text-xl text-gray-300">
              Tell us what you need and we will help match the right EMS or red-light configuration.
            </p>
            <p className="text-lg text-gray-400 mt-2">
              Direct answers. No pressure. Worldwide enquiries welcome.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">Get in Touch</h3>
                <p className="text-gray-400 mb-8">
                  Call during Australian business hours, email us, or message EFAM on WhatsApp.
                </p>
              </div>

              <div className="space-y-4">
                <a
                  href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center space-x-4 p-4 glass-card rounded-xl hover:border-blue-500 border border-transparent transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-500 group-hover:bg-blue-500/30 transition-colors">
                    <Phone className="text-blue-400" size={24} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Call ({contactInfo.phoneHours} Melbourne time)</div>
                    <div className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors">
                      {contactInfo.phone}
                    </div>
                  </div>
                </a>

                <a
                  href={`mailto:${contactInfo.email}?subject=EFAM%20equipment%20enquiry`}
                  className="flex items-center space-x-4 p-4 glass-card rounded-xl hover:border-blue-500 border border-transparent transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-500 group-hover:bg-blue-500/30 transition-colors">
                    <Mail className="text-blue-400" size={24} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Email</div>
                    <div className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors break-all">
                      {contactInfo.email}
                    </div>
                  </div>
                </a>

                <a
                  href={contactInfo.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 p-4 glass-card rounded-xl hover:border-blue-500 border border-transparent transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-500 group-hover:bg-blue-500/30 transition-colors">
                    <MessageCircle className="text-blue-400" size={24} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">WhatsApp</div>
                    <div className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors">
                      Message EFAM
                    </div>
                  </div>
                </a>
              </div>

              <div className="glass-card p-6 rounded-xl border-2 border-blue-500">
                <p className="text-white font-semibold mb-2">Need a quick answer?</p>
                <p className="text-gray-400 mb-4">Call EFAM during {contactInfo.phoneHours} Melbourne time.</p>
                <Button
                  className="w-full bg-blue-500 hover:bg-blue-400 text-white font-bold glow-button"
                  onClick={() => { window.location.href = `tel:${contactInfo.phone.replace(/\s/g, '')}`; }}
                >
                  <Phone className="mr-2" size={20} />
                  Call {contactInfo.phone}
                </Button>
              </div>
            </div>

            <div className="glass-card p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-white mb-3">Start an Equipment Enquiry</h3>
              <p className="text-sm text-gray-400 mb-6">
                Complete the details below and we will prepare the message in WhatsApp. You stay in control and press send there.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">Name *</label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-black/50 border-gray-700 text-white focus:border-blue-500 transition-colors"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">Email *</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-black/50 border-gray-700 text-white focus:border-blue-500 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-2">Phone *</label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-black/50 border-gray-700 text-white focus:border-blue-500 transition-colors"
                    placeholder="+61 412 345 678"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2">What are you looking for?</label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="bg-black/50 border-gray-700 text-white focus:border-blue-500 transition-colors resize-none"
                    placeholder="Home, studio or clinic? EMS, red light, or both? Tell us your country and what you need."
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-500 hover:bg-blue-400 text-white font-bold text-lg py-6 glow-button"
                >
                  <Send className="mr-2" size={20} />
                  Continue on WhatsApp
                </Button>
              </form>

              <p className="text-xs text-gray-500 mt-4 text-center">
                This form does not store your details on the website.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
